import { useAuth0 } from '@auth0/auth0-react'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { UserModelContext, useUserModel } from 'domain/users/user/user.model'

const UserContext = createContext<UserModelContext | undefined>(undefined)

/**
 * useUserContext hook returns a User context if it exists, otherwise throws an error
 * @returns User context
 * @category User
 */
export function useUserContext(): UserModelContext {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider')
  }
  return context
}

/**
 * UserProvider component provides a User context to its children
 * @param props
 * @category User
 */
export function UserProvider(props: Readonly<PropsWithChildren>): JSX.Element {
  const { user: aUser } = useAuth0()
  const userModelContext: UserModelContext = useUserModel({
    userId: aUser?.sub,
  })
  // use useMemo to avoid re-rendering the context provider
  const value = useMemo(() => ({ ...userModelContext }), [userModelContext])
  return <UserContext.Provider value={value} {...props} />
}
